import * as React from 'react'
import { Box, CircularProgress, Typography } from '@mui/joy'
import { Wrapper } from '@googlemaps/react-wrapper'
import type { IRoute } from '@numeo/types-core'
import type { TruckLocationHistoryRow } from '../TruckHistory'
import { SelectedTrucksMap } from './SelectedTrucksMap'

const SelectedTrucksMapLoading: React.FC = () => (
    <Box
        sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
        }}
    >
        <CircularProgress />
        <Typography level="body-sm">Loading map...</Typography>
    </Box>
)

const SelectedTrucksMapError: React.FC = () => (
    <Box
        sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 2,
        }}
    >
        <Typography level="body-sm">Unable to load Google Maps. Please check your internet connection and API key configuration.</Typography>
    </Box>
)

const SelectedTrucksEmptyState: React.FC = () => (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography level="body-sm">No trucks selected.</Typography>
    </Box>
)

interface SelectedTrucksMapSectionProps {
    trucks: TruckLocationHistoryRow[]
    activeVin: string | null
    focusedRow: TruckLocationHistoryRow | null
    loadRouteStops: IRoute[] | null
    loadRouteLoading: boolean
    loadRouteError: string | null
}

export const SelectedTrucksMapSection: React.FC<SelectedTrucksMapSectionProps> = ({ trucks, activeVin, focusedRow, loadRouteStops }) => {
    const hasTrucks = trucks.length > 0

    if (!hasTrucks) {
        return <SelectedTrucksEmptyState />
    }

    return (
        <Wrapper
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
            render={(status) => {
                if (status === 'LOADING') {
                    return <SelectedTrucksMapLoading />
                }

                if (status === 'FAILURE') {
                    return <SelectedTrucksMapError />
                }

                return <SelectedTrucksMap trucks={trucks} activeVin={activeVin} focusedRow={focusedRow} loadRouteStops={loadRouteStops} />
            }}
        />
    )
}
